import React, { useState, useEffect } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import EventCard from './event-card'
import TimeframeFilter from './timeframe-filter'
import CategoryFilter from './category-filter'

const WhatsOn = () => {
  // Initialize state from URL parameters
  const getInitialCategories = () => {
    const params = new URLSearchParams(window.location.search)
    const categories = params.getAll('categories[]')
    return categories.length > 0 ? categories : []
  }

  const getInitialTimeframe = () => {
    const params = new URLSearchParams(window.location.search)
    const timeframe = params.get('timeframe')
    return timeframe || 'all_future'
  }

  const [timeframe, setTimeframe] = useState(getInitialTimeframe())
  const [selectedCategories, setSelectedCategories] = useState(getInitialCategories())
  const [page, setPage] = useState(1)
  
  // Calculate perPage based on screen size to fill grid
  // 3 columns on lg, 2 on md, 1 on mobile
  // Use multiples to avoid empty spaces
  const perPage = 24 // Divisible by 1, 2, and 3

  // Build query params
  const buildQueryParams = () => {
    const params = new URLSearchParams()
    params.append('timeframe', timeframe)
    params.append('page', page)
    params.append('per_page', perPage)
    
    selectedCategories.forEach(cat => {
      params.append('categories[]', cat)
    })
    
    return params.toString()
  }

  // Fetch events data
  const { data, error, isLoading } = useSWR(
    `/events/whatson?${buildQueryParams()}`,
    fetcher
  )

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
  }, [timeframe, selectedCategories])

  // Store categories persistently to avoid jarring UI changes
  const [persistentCategories, setPersistentCategories] = useState([])
  const [persistentCategoryColors, setPersistentCategoryColors] = useState({})
  
  // Get unique categories and their colors from all loaded events
  const { allCategories, categoryColors } = React.useMemo(() => {
    if (!data) return { allCategories: [], categoryColors: {} }
    
    // If the API returns all_categories and all_category_colors, use those
    if (data.all_categories && data.all_category_colors) {
      return {
        allCategories: data.all_categories,
        categoryColors: data.all_category_colors
      }
    }
    
    // Fallback to discovering categories from events
    if (!data.events) return { allCategories: [], categoryColors: {} }
    
    const categoriesSet = new Set()
    const colors = {}
    
    data.events.forEach(event => {
      event.category_list?.forEach((cat, index) => {
        categoriesSet.add(cat)
        // Store the color for this category if we haven't seen it before
        if (!colors[cat] && event.category_colors?.[index]) {
          colors[cat] = event.category_colors[index]
        }
      })
    })
    
    return {
      allCategories: Array.from(categoriesSet).sort(),
      categoryColors: colors
    }
  }, [data])
  
  // Update persistent categories only when we have new data
  useEffect(() => {
    if (allCategories.length > 0) {
      setPersistentCategories(allCategories)
      setPersistentCategoryColors(categoryColors)
    }
  }, [allCategories, categoryColors])

  const handleCategoryToggle = (category) => {
    if (category === '__all__') {
      // Clear all selections to show all categories
      setSelectedCategories([])
    } else {
      setSelectedCategories(prev => {
        if (prev.includes(category)) {
          return prev.filter(c => c !== category)
        }
        return [...prev, category]
      })
    }
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    // Scroll to top of the list
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
        <p className="text-red-600 dark:text-red-400">
          Sorry, we couldn't load the events. Please try again later.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2 sm:space-y-4">
      {/* Filters Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6 space-y-4">
          <h2 className="text-2xl sm:text-3xl font-serif text-gray-900 dark:text-white">
            What's On
          </h2>
        
        {/* Timeframe Filter */}
        <TimeframeFilter 
          selected={timeframe} 
          onChange={setTimeframe} 
        />
        
        {/* Category Filter - Use persistent categories to avoid jarring changes */}
        {persistentCategories.length > 0 && (
          <CategoryFilter
            categories={persistentCategories}
            categoryColors={persistentCategoryColors}
            selected={selectedCategories}
            onToggle={handleCategoryToggle}
          />
        )}
      </div>

      {/* Results Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
          {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading events...</p>
          </div>
        ) : data?.events?.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-400">
              No events found for the selected filters.
            </p>
          </div>
        ) : (
          <>
            {/* Events Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4">
              {data?.events?.map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>

            {/* Pagination */}
            {data?.pagination?.total_pages > 1 && (
              <div className="mt-6 sm:mt-8 flex justify-center">
                <nav className="flex items-center gap-2 sm:gap-4">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="px-3 sm:px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600 text-sm sm:text-base transition-colors"
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </button>
                  
                  <span className="px-2 sm:px-4 py-2 text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                    <span className="hidden sm:inline">Page {data.pagination.current_page} of {data.pagination.total_pages}</span>
                    <span className="sm:hidden">{data.pagination.current_page}/{data.pagination.total_pages}</span>
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === data.pagination.total_pages}
                    className="px-3 sm:px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600 text-sm sm:text-base transition-colors"
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default WhatsOn